/**
 * GalleryListView
 *
 * Listenansicht für die Gallery.
 */

import { Heart, Tag, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthImageUrl } from '@/hooks/useAuthImageUrl';
import { Badge, Checkbox } from '@/components/v2/ui';
import { useApprovalActions } from '@/hooks/useApproval';
import { cn } from '@/lib/utils';
import type { GalleryImage } from '@/lib/api/gallery-v2';

interface GalleryListViewProps {
  images: GalleryImage[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onImageClick?: (image: GalleryImage) => void;
  showApprovalActions?: boolean;
}

export function GalleryListView({
  images,
  selectedIds,
  onToggleSelect,
  onToggleFavorite,
  onImageClick,
  showApprovalActions = false,
}: GalleryListViewProps) {
  const { t } = useTranslation();
  return (
    <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50 text-sm font-medium text-neutral-500">
        <div className="w-8" />
        <div>{t('common.image')}</div>
        <div className="w-24">{t('gallery.tags')}</div>
        <div className="w-32">Template</div>
        <div className="w-28">Erstellt</div>
        <div className="w-16" />
      </div>

      {/* Rows */}
      {images.map((image) => (
        <ListRow
          key={image.id}
          image={image}
          isSelected={selectedIds.has(image.id)}
          onToggleSelect={() => onToggleSelect(image.id)}
          onToggleFavorite={() => onToggleFavorite(image.id)}
          onClick={() => onImageClick?.(image)}
          showApprovalActions={showApprovalActions}
        />
      ))}
    </div>
  );
}

interface ListRowProps {
  image: GalleryImage;
  isSelected: boolean;
  onToggleSelect: () => void;
  onToggleFavorite: () => void;
  onClick?: () => void;
  showApprovalActions?: boolean;
}

function ListRow({
  image,
  isSelected,
  onToggleSelect,
  onToggleFavorite,
  onClick,
  showApprovalActions = false,
}: ListRowProps) {
  const { t } = useTranslation();
  const { authUrl } = useAuthImageUrl();
  const { updateSingle, isUpdatingSingle } = useApprovalActions();

  const handleApprove = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateSingle({ imageId: image.id, action: 'approve' });
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateSingle({ imageId: image.id, action: 'reject' });
  };
  const displayName =
    image.display_name || image.prompt?.slice(0, 50) || 'Unbenannt';
  const createdDate = new Date(image.created_at).toLocaleDateString('de-DE');

  return (
    <div
      className={cn(
        'grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 px-4 py-3 border-b border-neutral-100 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors',
        isSelected && 'bg-orange-50 dark:bg-orange-900/20'
      )}
    >
      {/* Checkbox */}
      <div className="flex items-center" onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}>
        <Checkbox checked={isSelected} onChange={onToggleSelect} />
      </div>

      {/* Image + Name */}
      <div className="flex items-center gap-3 min-w-0 cursor-pointer" onClick={onClick}>
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800 flex-shrink-0">
          {image.image_url ? (
            <img
              src={authUrl(`/thumb/${image.id}`)}
              alt={image.alt_title || displayName}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-neutral-400">
              ?
            </div>
          )}
        </div>
        <div className="min-w-0">
          <p className="font-medium text-neutral-900 dark:text-neutral-100 truncate">
            {displayName}
          </p>
          <p className="text-sm text-neutral-500 truncate">
            {image.ratio || '?'} • {image.file_format?.toUpperCase() || '?'}
          </p>
        </div>
      </div>

      {/* Tags */}
      <div className="flex items-center w-24">
        {image.tag_count > 0 ? (
          <Badge variant="secondary" className="gap-1">
            <Tag className="h-3 w-3" />
            {image.tag_count}
          </Badge>
        ) : (
          <span className="text-neutral-400">-</span>
        )}
      </div>

      {/* Template */}
      <div className="flex items-center w-32">
        {image.template_name ? (
          <span className="text-sm text-neutral-600 dark:text-neutral-400 truncate">
            {image.template_name}
          </span>
        ) : (
          <span className="text-neutral-400">-</span>
        )}
      </div>

      {/* Created */}
      <div className="flex items-center w-28 text-sm text-neutral-500">
        {createdDate}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 justify-end">
        {showApprovalActions && (
          <>
            <button
              onClick={handleReject}
              disabled={isUpdatingSingle}
              className="p-1.5 rounded-lg transition-colors text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
              title={t('common.reject')}
            >
              <XCircle className="h-4 w-4" />
            </button>
            <button
              onClick={handleApprove}
              disabled={isUpdatingSingle}
              className="p-1.5 rounded-lg transition-colors text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50"
              title={t('common.approve')}
            >
              <CheckCircle className="h-4 w-4" />
            </button>
          </>
        )}
        <button
          onClick={onToggleFavorite}
          className={cn(
            'p-1.5 rounded-lg transition-colors',
            image.is_favorite
              ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
              : 'text-neutral-400 hover:text-red-500 hover:bg-neutral-100 dark:hover:bg-neutral-800'
          )}
        >
          <Heart
            className={cn('h-4 w-4', image.is_favorite && 'fill-current')}
          />
        </button>
      </div>
    </div>
  );
}
